/**
 * ProposalBodyEditor — the "Generate body" seam between the Company Brain
 * and a proposal document.
 *
 * "Generate body" runs the PURE `composeProposalBody` over the account's
 * live Brain facts + the doc's real line items (derived from its recorded
 * value — never fabricated) and fills the editable markdown body with the
 * result. The honesty contract holds end-to-end: sections whose source
 * facts are absent are omitted, and the output is labeled "Drafted from
 * your Brand Brain — edit before sending", never presented as final copy.
 *
 * Overwrite guard: a hand-written (or hand-edited) body is NEVER replaced
 * silently — `needsOverwriteConfirm` gates regeneration behind a confirm.
 * Saving persists through the existing documents store (`updateDocument`),
 * so the in-app Preview modal picks the sections up immediately.
 */

import { useEffect, useState } from 'react';
import { Save, Sparkles } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { subscribeBrain } from '../lib/brain/companyBrain';
import {
  composeProposalBody,
  needsOverwriteConfirm,
  serializeProposalBody,
} from '../lib/docs/proposalBody';
import { defaultLineItemsForDoc } from '../lib/crm/proposalDoc';
import { updateDocument } from '../lib/db/documents';
import type { Account, BrainFact, BusinessDoc } from '../lib/db/types';

interface Props {
  doc: BusinessDoc;
  /** The linked account record (POC + name feed the composition). */
  account: Account | null;
  canMutate: boolean;
}

export function ProposalBodyEditor({ doc, account, canMutate }: Props) {
  const [body, setBody] = useState(doc.body);
  const [lastGenerated, setLastGenerated] = useState<string | null>(null);
  const [facts, setFacts] = useState<BrainFact[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync local state when a different document opens.
  useEffect(() => {
    setBody(doc.body);
    setLastGenerated(null);
    setError(null);
  }, [doc.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live Brain facts for the linked account — the ONLY prose source.
  useEffect(() => {
    if (doc.entityType !== 'account' || !doc.entityId) {
      setFacts([]);
      return;
    }
    return subscribeBrain(doc.entityId, (view) =>
      setFacts(view.sections.flatMap((s) => s.facts)),
    );
  }, [doc.entityType, doc.entityId]);

  const handleGenerate = () => {
    // Never silently overwrite hand-written content.
    if (needsOverwriteConfirm(body, lastGenerated)) {
      const ok = window.confirm(
        'Replace the current body with a fresh draft from the Brand Brain? Your hand-written edits will be lost.',
      );
      if (!ok) return;
    }
    const sections = composeProposalBody(
      account,
      facts,
      defaultLineItemsForDoc(doc),
    );
    const md = serializeProposalBody(sections);
    setBody(md);
    setLastGenerated(md);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateDocument(doc.id, { body });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the body.');
    } finally {
      setSaving(false);
    }
  };

  const dirty = body !== doc.body;

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 10 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <span className="t-eyebrow">Proposal body</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="glass-btn"
            onClick={handleGenerate}
            disabled={!canMutate}
            title="Compose the body from this account's Brain facts + the recorded line items — sections without source facts are omitted"
          >
            <Sparkles size={13} /> Generate body
          </button>
          <button
            type="button"
            className="glass-btn glass-btn--primary"
            onClick={() => void handleSave()}
            disabled={!canMutate || !dirty || saving}
          >
            <Save size={13} /> {saving ? 'Saving…' : 'Save body'}
          </button>
        </div>
      </div>

      {lastGenerated !== null && (
        <p className="t-meta" style={{ margin: 0, color: C.magentaSoft }}>
          Drafted from your Brand Brain — edit before sending.
        </p>
      )}

      <textarea
        className="glass-input"
        aria-label="Proposal body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        disabled={!canMutate}
        rows={10}
        placeholder={
          'Write the proposal body here, or use "Generate body" to draft it from the Brand Brain.'
        }
        style={{
          width: '100%',
          resize: 'vertical',
          fontFamily: 'inherit',
          lineHeight: 1.5,
          minHeight: 160,
        }}
      />

      <p className="t-meta" style={{ margin: 0 }}>
        Composed only from Brain facts, this account&apos;s record, and the
        recorded line items — nothing is invented; sections without source
        facts are omitted. Sections (## Executive summary, ## About us, ##
        Scope of work, ## Why us, ## Next steps) render inside the branded
        Preview.
      </p>

      {error && (
        <p className="t-meta" role="alert" style={{ margin: 0, color: '#ffb4b4' }}>
          {error}
        </p>
      )}
    </GlassPanel>
  );
}
